import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { push } from 'connected-react-router';
import moment from 'moment';
import FormViewButton from 'components/Common/Buttons/FormViewButton';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import FormTypes from 'common/Forms/FormTypes';

const LinkedImyStructure = ({ structure, permissions }) => {
  const dispatch = useDispatch();

  const navigateToImyStructure = (form) => {
    if (form.topLevelAccountId !== null) {
      let uri = `/w8imystructure/${ form.topLevelAccountId }`;
      if (form.whsVersion !== null && form.whsVersion > 0) {
        uri += `/${ form.whsVersion }`;
      }
      dispatch(push(uri));
    }
  };

  const navigateToDigitalWhsStatement = (form) => {
    if (form.topLevelAccountId !== null) {
      let uri = `/whd-statement/${ form.topLevelAccountId }`;
      if (form.whsVersion !== null && form.whsVersion > 0) {
        uri += `/${ form.whsVersion }`;
      }
      dispatch(push(uri));
    }
  };

  const buildFormObject = () => {
    const formType = structure && structure.formType && FormTypes.getByValue(structure.formType);
    const formObject = {
      accountId: structure.topLevelAccountId,
      formId: structure.formId,
      formType: formType && formType.code,
      workflowStatus: structure.workflowStatus,
      workflowUsername: structure.workflowUsername ? structure.workflowUsername : 'N/A',
      workflowUserFullName: structure.workflowUserFullName
    };
    return formObject;
  };

  function dateFormatter(value, format) {
    return moment(value).format(format);
  }

  function getEffectiveDate(date) {
    if (date !== null) {
      return `${ dateFormatter(date, 'MMM DD, YYYY') }`;
    }
    return 'N/A';
  }

  function getEffectiveToDate(date) {
    if (date !== null) {
      return `${ dateFormatter(date, 'MMM DD, YYYY') }`;
    }
    return 'Not set';
  }

  const hasPublishedVersion = !!(structure && structure.whsVersion > 0);
  const missingPublishedVersion = structure && structure.hasAllocationStatement && !hasPublishedVersion;
  const isNoLongerEffective = structure && structure.hasAllocationStatement && !structure.isApplicable;
  const viewingLastPublished = isNoLongerEffective && hasPublishedVersion;
  const viewButtonsDisabled = isNoLongerEffective && !hasPublishedVersion;

  return (
    <div style={ { display: 'flex' } } key={ structure.formId }>
      <div className="col-5 mr-10">
        <div className="columns" style={ { margin: 0 } }>
          <div className="col-4 panel-item-name"><b><p id="linked-imy-intermediary-name-name">Intermediary Name</p></b></div>
          <div className="col-8 panel-item-value"><p id="linked-imy-intermediary-name-value">{ structure.topLevelCustomerName }</p></div>
        </div>
        <div className="columns" style={ { margin: 0 } }>
          <div className="col-4 panel-item-name"><b><p id="linked-imy-intermediary-account-name">Intermediary Account</p></b></div>
          <div className="col-8 panel-item-value"><p id="linked-imy-intermediary-account-value">{ structure.topLevelAccount }</p></div>
        </div>
        <div className="columns" style={ { margin: 0 } }>
          <div className="col-4 panel-item-name"><b><p id="linked-imy-intermediary-uploaded-date-name">Uploaded</p></b></div>
          <div className="col-8 panel-item-value"><p id="linked-imy-intermediary-uploaded-date-value">{ getEffectiveDate(structure.uploadedDate) }</p></div>
        </div>
        <div className="columns" style={ { margin: 0 } }>
          <div className="col-4 panel-item-name"><b><p id="linked-imy-intermediary-linked-date-name">Linked</p></b></div>
          <div className="col-8 panel-item-value"><p id="linked-imy-intermediary-linked-date-value">{ getEffectiveDate(structure.applicableFrom) }</p></div>
        </div>
        <div className="columns" style={ { margin: 0 } }>
          <div className="col-4 panel-item-name"><b><p id="linked-imy-intermediary-effective-to-date-name">Effective To</p></b></div>
          <div className="col-8 panel-item-value"><p id="linked-imy-intermediary-effective-to-date-value">{ getEffectiveToDate(structure.applicableTo) }</p></div>
        </div>
      </div>
      <div className="col-7">
        <p>View options</p>
        <div className="col-8 col-2x-12">
          { structure.formId > 0 && AccessControl.hasPermission(permissions, [ActivityPerms.scannedView, ActivityPerms.scannedEdit, ActivityPerms.scannedValidate])
               && <FormViewButton
                 permissions={ permissions }
                 formId={ structure.formId }
                 form={ buildFormObject() }
                 accountId={ structure.topLevelAccountId }
                 content={ <React.Fragment><i className="icon icon-apps mr-2" />IMY Form</React.Fragment> }
                 className="btn btn-secondary mr-10"
                 title="View IMY Form"
               /> }
          { structure.hasAllocationStatement && AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewTree]) && <button
            className="btn mr-10"
            type="button"
            title={ viewingLastPublished ? 'View last published IMY Tree (read-only)' : 'View IMY Tree' }
            onClick={ () => navigateToImyStructure(structure) }
            id="linked-imy-intermediary-view-tree-button"
            disabled={ viewButtonsDisabled }
          >
            <span className="icon icon-treeview mr-2" />
            { viewingLastPublished ? 'IMY Tree (last published)' : 'IMY Tree' }
            </button> }
          { structure.hasAllocationStatement && AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrViewWhs]) && <button
            className="btn"
            type="button"
            title={ viewingLastPublished ? 'View last published Digital Statement (read-only)' : 'View Digital Statement' }
            onClick={ () => navigateToDigitalWhsStatement(structure) }
            id="linked-imy-intermediary-view-whs-button"
            disabled={ viewButtonsDisabled }
          >
            { viewingLastPublished ? 'WHS (last published)' : 'WHS' }
            </button> }
          { viewingLastPublished
            && <div className="mt-10 toast toast-info mb-10 text-center" id="linked-imy-warning-whs-no-longer-effective">
              The Withholding Statement is no longer effective for this account. You can view the last published IMY Tree and WHS (read-only).
            </div> }
          { missingPublishedVersion && !structure.isApplicable
            && <div className="mt-10 toast toast-info mb-10 text-center" id="linked-imy-warning-no-published-version">
              The Linked IMY Tree Structure does not have a published version for this account.
            </div> }
        </div>
      </div>
    </div>
  );
};

LinkedImyStructure.propTypes = {
  structure: PropTypes.shape().isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string)
};

LinkedImyStructure.defaultProps = {
  permissions: null
};

export default LinkedImyStructure;
